import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useProject } from '../../../contexts/ProjectContext';
import {
  FiClock as Clock,
  FiPlus as Plus,
  FiTrash2 as Trash2,
  FiEdit2 as Edit2,
  FiPlay as Play,
  FiRefreshCw as RefreshCw,
  FiCalendar as Calendar,
  FiChevronDown as ChevronDown,
  FiChevronRight as ChevronRight,
  FiCheckCircle as CheckCircle,
  FiXCircle as XCircle,
  FiAlertTriangle as AlertTriangle,
  FiTrendingUp as TrendingUp,
  FiTrendingDown as TrendingDown,
  FiLoader as Loader
} from 'react-icons/fi';

interface SchedulesTabProps {
  apiBaseUrl: string;
  clientId: string;
}

interface Schedule {
  id: string;
  name?: string;
  description?: string;
  frequency?: string;
  is_enabled: boolean;
  prompt_id?: string;
  project_id?: string | null;
  dataset_id?: string;
  last_run_at?: string;
  next_run_at?: string;
  last_run_status?: string;
  last_run_score?: number;
  previous_score?: number;
  scoring_method?: string;
  model_id?: string;
  run_count?: number;
  has_drift?: boolean;
  drift_pass_rate_threshold?: number;
  drift_latency_threshold?: number;
  drift_cost_threshold?: number;
  alert_on_drift?: boolean;
  notify_on_drift?: boolean;
  baseline?: {
    avg_pass_rate: number;
    avg_score?: number;
    avg_latency_ms?: number;
    sample_count?: number;
  } | null;
  credential_id?: string;
  provider?: string;
  pass_threshold?: number;
  judge_rubric_id?: string;
  similarity_threshold?: number;
  run_assertions?: boolean;
  schedule_time?: string;
  schedule_day?: number;
}

// Frequency display config
const FREQUENCY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  hourly: { label: 'Hourly', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  daily: { label: 'Daily', color: 'text-green-400', bg: 'bg-green-500/20' },
  weekly: { label: 'Weekly', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  on_deploy: { label: 'On Deploy', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  on_commit: { label: 'On Commit', color: 'text-orange-400', bg: 'bg-orange-500/20' }
};

const SchedulesTab: React.FC<SchedulesTabProps> = ({ apiBaseUrl, clientId }) => {
  const { selectedProjectId, projects, hasProjectAccess } = useProject();
  // State
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningScheduleId, setRunningScheduleId] = useState<string | null>(null);

  // Modal state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);

  // Available prompts, datasets, credentials and models for selection
  const [prompts, setPrompts] = useState<ScheduleListItem[]>([]);
  const [datasets, setDatasets] = useState<ScheduleListItem[]>([]);
  const [credentials, setCredentials] = useState<ScheduleCredential[]>([]);
  const [allModels, setAllModels] = useState<ScheduleModel[]>([]);
  const [rubrics, setRubrics] = useState<ScheduleRubric[]>([]);

  // Helper to get auth headers
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const projectQuery = selectedProjectId ? `&project_id=${selectedProjectId}` : '';
      const [schedulesRes, promptsRes, datasetsRes, credentialsRes, modelsRes, rubricsRes] = await Promise.all([
        fetch(`${apiBaseUrl}/schedules?client_id=${clientId}${projectQuery}`, { headers }),
        fetch(`${apiBaseUrl}/prompts?client_id=${clientId}`, { headers }),
        fetch(`${apiBaseUrl}/datasets?client_id=${clientId}`, { headers }),
        fetch(`${apiBaseUrl}/credentials?client_id=${clientId}`, { headers }),
        fetch(`${apiBaseUrl}/credentials/available-models?client_id=${clientId}`, { headers }),
        fetch(`${apiBaseUrl}/judge-rubrics?client_id=${clientId}`, { headers })
      ]);

      if (schedulesRes.ok) setSchedules(await schedulesRes.json());
      if (promptsRes.ok) {
        const promptsData = await promptsRes.json();
        setPrompts(promptsData.data || promptsData || []);
      }
      if (datasetsRes.ok) {
        const datasetsData = await datasetsRes.json();
        setDatasets(datasetsData.data || datasetsData || []);
      }
      if (credentialsRes.ok) setCredentials(await credentialsRes.json());
      if (modelsRes.ok) {
        const modelsData = await modelsRes.json();
        setAllModels(modelsData.models || []);
      }
      if (rubricsRes.ok) {
        const rubricsData = await rubricsRes.json();
        setRubrics(rubricsData.items || rubricsData.data || rubricsData || []);
      }
    } catch (err: unknown) {
      setError('Failed to load schedules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, clientId, getAuthHeaders, selectedProjectId]);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Schedule handlers
  const handleSaveSchedule = async (scheduleData: Record<string, unknown>) => {
    try {
      const url = editingSchedule
        ? `${apiBaseUrl}/schedules/${editingSchedule.id}`
        : `${apiBaseUrl}/schedules`;

      const res = await fetch(url, {
        method: editingSchedule ? 'PATCH' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...scheduleData, client_id: clientId })
      });

      if (res.ok) {
        await fetchData();
        setShowScheduleModal(false);
        setEditingSchedule(null);
      }
    } catch (err: unknown) {
      console.error('Failed to save schedule:', err);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!globalThis.confirm('Delete this evaluation schedule?')) return;

    try {
      await fetch(`${apiBaseUrl}/schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      await fetchData();
    } catch (err: unknown) {
      console.error('Failed to delete schedule:', err);
    }
  };

  const handleToggleSchedule = async (schedule: Schedule) => {
    try {
      await fetch(`${apiBaseUrl}/schedules/${schedule.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_enabled: !schedule.is_enabled })
      });
      await fetchData();
    } catch (err: unknown) {
      console.error('Failed to toggle schedule:', err);
    }
  };

  const handleRunNow = async (scheduleId: string) => {
    setRunningScheduleId(scheduleId);
    try {
      const res = await fetch(`${apiBaseUrl}/schedules/${scheduleId}/run`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        await res.json();
        toast.success(
          <div>
            <strong>Evaluation started!</strong>
            <p className="text-sm opacity-80">Check the Test Runs tab for results.</p>
          </div>,
          { duration: 4000 }
        );
        await fetchData();
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to trigger evaluation');
      }
    } catch (err: unknown) {
      console.error('Failed to trigger evaluation:', err);
      toast.error('Failed to trigger evaluation');
    } finally {
      setRunningScheduleId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
        <span className="ml-2 text-gray-400">Loading schedules...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-400" />
            Scheduled Evaluations
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Configure automatic evaluation runs to detect drift and regressions
          </p>
        </div>
        <button
          onClick={() => {
            setEditingSchedule(null);
            setShowScheduleModal(true);
          }}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Create Schedule
        </button>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Schedules List */}
      {schedules.length === 0 ? (
        <div className="text-center py-12 bg-gray-800/50 rounded-lg border border-gray-700">
          <Clock className="w-12 h-12 mx-auto mb-4 text-gray-600" />
          <h3 className="text-lg font-medium text-white mb-2">No Scheduled Evaluations</h3>
          <p className="text-gray-400 mb-4">
            Set up automatic evaluation runs to continuously monitor your AI performance
          </p>
          <button
            onClick={() => {
              setEditingSchedule(null);
              setShowScheduleModal(true);
            }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Your First Schedule
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {schedules.map(schedule => (
            <ScheduleCard
              key={schedule.id}
              schedule={schedule}
              prompts={prompts}
              datasets={datasets}
              isRunning={runningScheduleId === schedule.id}
              projectName={schedule.project_id ? (projects.find((project) => project.id === schedule.project_id)?.name ?? 'Project') : null}
              hasProjectAccess={hasProjectAccess}
              onEdit={() => {
                setEditingSchedule(schedule);
                setShowScheduleModal(true);
              }}
              onDelete={() => handleDeleteSchedule(schedule.id)}
              onToggle={() => handleToggleSchedule(schedule)}
              onRunNow={() => handleRunNow(schedule.id)}
            />
          ))}
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && (
        <ScheduleModal
          schedule={editingSchedule}
          prompts={prompts}
          datasets={datasets}
          credentials={credentials}
          allModels={allModels}
          rubrics={rubrics}
          onSave={handleSaveSchedule}
          onClose={() => {
            setShowScheduleModal(false);
            setEditingSchedule(null);
          }}
        />
      )}
    </div>
  );
};

interface ScheduleListItem {
  id: string;
  name?: string;
  content_type?: string;
  item_count?: number;
  test_case_count?: number;
}

interface ScheduleCredential {
  id: string;
  name: string;
  provider: string;
  is_enabled?: boolean;
}

interface ScheduleModel {
  id: string;
  name?: string;
  provider: string;
}

interface ScheduleRubric {
  id: string;
  name: string;
}

interface ScheduleCardProps {
  schedule: Schedule;
  prompts: ScheduleListItem[];
  datasets: ScheduleListItem[];
  isRunning: boolean;
  projectName?: string | null;
  hasProjectAccess?: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  onRunNow: () => void;
}

// Schedule Card Component
const ScheduleCard: React.FC<ScheduleCardProps> = ({ schedule, prompts, datasets, isRunning, projectName, hasProjectAccess = false, onEdit, onDelete, onToggle, onRunNow }) => {
  const [expanded, setExpanded] = useState(false);
  const frequency = FREQUENCY_CONFIG[schedule.frequency || 'daily'] || FREQUENCY_CONFIG.daily;

  const prompt = prompts.find(p => p.id === schedule.prompt_id);
  const dataset = datasets.find(d => d.id === schedule.dataset_id);

  const getDriftIndicator = () => {
    if (!schedule.has_drift) return null;

    return (
      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center gap-1">
        <TrendingDown className="w-3 h-3" />
        Drift Detected
      </span>
    );
  };

  const formatNextRun = () => {
    if (!schedule.next_run_at) return 'Not scheduled';
    // Ensure we interpret the date as UTC (backend stores in UTC without 'Z' suffix)
    const utcDateStr = schedule.next_run_at.endsWith('Z')
      ? schedule.next_run_at
      : schedule.next_run_at + 'Z';
    const nextRun = new Date(utcDateStr);
    const now = new Date();
    const diffMs = nextRun.getTime() - now.getTime();

    if (diffMs < 0) return 'Overdue';
    if (diffMs < 60000) return 'Less than a minute';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)} minutes`;
    if (diffMs < 86400000) {
      const hours = Math.floor(diffMs / 3600000);
      const mins = Math.floor((diffMs % 3600000) / 60000);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours} hours`;
    }
    // Show local time with timezone indicator
    return nextRun.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format for tooltip showing full date/time
  const getNextRunTooltip = () => {
    if (!schedule.next_run_at) return '';
    // Ensure we interpret the date as UTC (backend stores in UTC without 'Z' suffix)
    const utcDateStr = schedule.next_run_at.endsWith('Z')
      ? schedule.next_run_at
      : schedule.next_run_at + 'Z';
    const nextRun = new Date(utcDateStr);
    return nextRun.toLocaleString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  return (
    <div className={`bg-gray-800/50 rounded-lg border ${schedule.is_enabled ? 'border-gray-700' : 'border-gray-800 opacity-60'}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Toggle */}
            <button
              onClick={onToggle}
              className={`w-10 h-6 rounded-full transition-colors ${
                schedule.is_enabled ? 'bg-blue-500' : 'bg-gray-600'
              }`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                schedule.is_enabled ? 'translate-x-5' : 'translate-x-1'
              }`} />
            </button>

            {/* Schedule Info */}
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-white">{schedule.name}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${frequency.bg} ${frequency.color}`}>
                  {frequency.label}
                </span>
                {hasProjectAccess && projectName && (
                  <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-300">
                    {projectName}
                  </span>
                )}
                {getDriftIndicator()}
              </div>
              <div className="text-sm text-gray-400 mt-1">
                {prompt?.name || 'Unknown prompt'} • {dataset?.name || 'Unknown dataset'}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {schedule.is_enabled && (
              <button
                onClick={onRunNow}
                disabled={isRunning}
                className={`px-3 py-1.5 rounded flex items-center gap-1.5 text-sm transition-colors ${
                  isRunning
                    ? 'bg-green-500/10 text-green-400/60 cursor-not-allowed'
                    : 'bg-green-500/20 hover:bg-green-500/30 text-green-400'
                }`}
              >
                {isRunning ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Run Now
                  </>
                )}
              </button>
            )}
            <button
              onClick={onEdit}
              className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-2 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="mt-3 flex items-center gap-6 text-sm text-gray-500">
          <span className="flex items-center gap-1" title={getNextRunTooltip()}>
            <Clock className="w-4 h-4" />
            Next: {formatNextRun()}
          </span>
          <span>Runs: {schedule.run_count || 0}</span>
          {schedule.last_run_status && (
            <span className="flex items-center gap-1">
              {schedule.last_run_status === 'completed' ? (
                <CheckCircle className="w-4 h-4 text-green-400" />
              ) : (
                <XCircle className="w-4 h-4 text-red-400" />
              )}
              Last: {schedule.last_run_status}
            </span>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-gray-700 p-4 space-y-4">
          {/* Drift Detection Settings */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Drift Detection</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-900/50 rounded p-3">
                <div className="text-xs text-gray-500">Pass Rate Threshold</div>
                <div className="text-white">{((schedule.drift_pass_rate_threshold || 0.1) * 100).toFixed(0)}% drop</div>
              </div>
              <div className="bg-gray-900/50 rounded p-3">
                <div className="text-xs text-gray-500">Latency Threshold</div>
                <div className="text-white">{((schedule.drift_latency_threshold || 0.5) * 100).toFixed(0)}% increase</div>
              </div>
              <div className="bg-gray-900/50 rounded p-3">
                <div className="text-xs text-gray-500">Cost Threshold</div>
                <div className="text-white">{((schedule.drift_cost_threshold || 0.5) * 100).toFixed(0)}% increase</div>
              </div>
            </div>
          </div>

          {/* Baseline Info */}
          {schedule.baseline && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Current Baseline</h4>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-gray-900/50 rounded p-3">
                  <div className="text-xs text-gray-500">Avg Pass Rate</div>
                  <div className="text-white">{(schedule.baseline.avg_pass_rate * 100).toFixed(1)}%</div>
                </div>
                <div className="bg-gray-900/50 rounded p-3">
                  <div className="text-xs text-gray-500">Avg Score</div>
                  <div className="text-white">{schedule.baseline.avg_score?.toFixed(2) || 'N/A'}</div>
                </div>
                <div className="bg-gray-900/50 rounded p-3">
                  <div className="text-xs text-gray-500">Avg Latency</div>
                  <div className="text-white">{schedule.baseline.avg_latency_ms?.toFixed(0) || 'N/A'}ms</div>
                </div>
                <div className="bg-gray-900/50 rounded p-3">
                  <div className="text-xs text-gray-500">Sample Size</div>
                  <div className="text-white">{schedule.baseline.sample_count || 0} runs</div>
                </div>
              </div>
            </div>
          )}

          {/* Alert Integration */}
          {schedule.alert_on_drift && (
            <div className="flex items-center gap-2 text-sm text-yellow-400">
              <AlertTriangle className="w-4 h-4" />
              Alerts enabled when drift is detected
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Scoring method options
const SCORING_METHODS = [
  { value: 'exact_match', label: 'Exact Match' },
  { value: 'contains', label: 'Contains' },
  { value: 'similarity', label: 'Similarity' },
  { value: 'semantic_similarity', label: 'Semantic Similarity' },
  { value: 'llm_judge', label: 'LLM Judge' }
];

interface ScheduleModalProps {
  schedule: Schedule | null;
  prompts: ScheduleListItem[];
  datasets: ScheduleListItem[];
  credentials: ScheduleCredential[];
  allModels: ScheduleModel[];
  rubrics: ScheduleRubric[];
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

// Schedule Modal Component
const ScheduleModal: React.FC<ScheduleModalProps> = ({ schedule, prompts, datasets, credentials, allModels, rubrics, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    name: schedule?.name || '',
    description: schedule?.description || '',
    prompt_id: schedule?.prompt_id || '',
    dataset_id: schedule?.dataset_id || '',
    // LLM Configuration
    credential_id: schedule?.credential_id || '',
    provider: schedule?.provider || 'openai',
    model_id: schedule?.model_id || '',
    // Scoring Configuration
    scoring_method: schedule?.scoring_method || 'contains',
    pass_threshold: schedule?.pass_threshold || 0.7,
    judge_rubric_id: schedule?.judge_rubric_id || '',
    similarity_threshold: schedule?.similarity_threshold || 0.8,
    run_assertions: schedule?.run_assertions ?? true,
    // Schedule Configuration
    frequency: schedule?.frequency || 'daily',
    schedule_time: schedule?.schedule_time || '03:00',
    schedule_day: schedule?.schedule_day || 0,
    // Drift Detection
    drift_pass_rate_threshold: schedule?.drift_pass_rate_threshold || 0.1,
    drift_latency_threshold: schedule?.drift_latency_threshold || 0.5,
    drift_cost_threshold: schedule?.drift_cost_threshold || 0.5,
    notify_on_drift: schedule?.notify_on_drift ?? true,
    is_enabled: schedule?.is_enabled ?? true
  });

  // Filter models based on selected credential's provider
  const selectedCredential = credentials.find(c => c.id === formData.credential_id);
  const filteredModels = allModels.filter(m =>
    !selectedCredential || m.provider === selectedCredential.provider
  );

  // When credential changes, update provider and clear model if incompatible
  const handleCredentialChange = (credentialId: string) => {
    const cred = credentials.find(c => c.id === credentialId);
    setFormData(prev => ({
      ...prev,
      credential_id: credentialId,
      provider: cred?.provider || prev.provider,
      model_id: '' // Clear model selection when provider changes
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validate required fields
    if (!formData.credential_id) {
      alert('Please select a credential');
      return;
    }
    if (!formData.model_id) {
      alert('Please select a model');
      return;
    }
    // Clean data - convert empty strings to null for optional UUID fields
    const cleanedData = {
      ...formData,
      prompt_id: formData.prompt_id || null,
      judge_rubric_id: formData.judge_rubric_id || null,
    };
    onSave(cleanedData);
  };

  const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg w-full max-w-2xl mx-4 border border-gray-700 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
          <h3 className="text-lg font-medium text-white">
            {schedule ? 'Edit Schedule' : 'Create Evaluation Schedule'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* How Evaluations Work - Collapsible Help */}
          <details className="bg-gray-900/50 rounded-lg border border-gray-700">
            <summary className="px-3 py-2 font-medium text-gray-300 cursor-pointer hover:bg-gray-700/50 rounded-lg flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-blue-400" />
              How do scheduled evaluations work?
            </summary>
            <div className="px-3 pb-3 text-xs text-gray-400 space-y-1">
              <p><strong className="text-gray-300">1. Dataset:</strong> Choose a dataset with test cases, Q&A pairs, or documents.</p>
              <p><strong className="text-gray-300">2. Prompt (Optional):</strong> Variables like {'{{question}}'} are replaced with dataset values.</p>
              <p><strong className="text-gray-300">3. Execution:</strong> Each item is sent to the LLM at the scheduled time.</p>
              <p><strong className="text-gray-300">4. Drift Detection:</strong> Results are compared against baselines to detect regressions.</p>
            </div>
          </details>

          {/* Name */}
          <div>
            <label htmlFor="sched-name" className="block text-sm font-medium text-gray-300 mb-1">
              Schedule Name
            </label>
            <input
                                                                                  id="sched-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Daily Regression Test"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500"
              required
            />
          </div>

          {/* Dataset Selection */}
          <div>
            <label htmlFor="sched-dataset" className="block text-sm font-medium text-gray-300 mb-1">
              Dataset <span className="text-red-400">*</span>
            </label>
            <select
                                                                                  id="sched-dataset"
              value={formData.dataset_id}
              onChange={(e) => setFormData({ ...formData, dataset_id: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
              required
            >
              <option value="">Select dataset...</option>
              {datasets.map(d => {
                const contentType = d.content_type || 'test_cases';
                const itemCount = d.item_count || d.test_case_count || 0;
                const typeLabelMap: Record<string, string> = {
                  test_cases: 'Test Cases',
                  documents: 'Documents',
                  qa_pairs: 'Q&A Pairs',
                  mixed: 'Mixed'
                };
                const typeLabel = typeLabelMap[contentType] || contentType;
                return (
                  <option key={d.id} value={d.id}>
                    {d.name} ({itemCount} items) [{typeLabel}]
                  </option>
                );
              })}
            </select>
            {/* Warning for documents datasets */}
            {formData.dataset_id && datasets.find(d => d.id === formData.dataset_id)?.content_type === 'documents' && (
              <div className="mt-2 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-sm text-yellow-400">
                <strong>Note:</strong> Document datasets don't have expected outputs. Use Semantic Similarity or LLM Judge for best results.
              </div>
            )}
          </div>

          {/* Prompt Selection */}
          <div>
            <label htmlFor="sched-prompt-template" className="block text-sm font-medium text-gray-300 mb-1">
              Prompt Template <span className="text-gray-500 font-normal">(Optional)</span>
            </label>
            <select
                                                                                  id="sched-prompt-template"
              value={formData.prompt_id}
              onChange={(e) => setFormData({ ...formData, prompt_id: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
            >
              <option value="">No prompt - use input directly</option>
              {prompts.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {/* Prompt Usage Help */}
            <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs text-blue-300">
              <strong>With prompt:</strong> Variables like {'{{question}}'} are replaced with dataset values.{' '}
              <strong>Without prompt:</strong> Input is sent directly to the LLM.
            </div>
          </div>

          {/* LLM Configuration Section */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">LLM Configuration</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="sched-credential" className="block text-sm font-medium text-gray-300 mb-1">
                  Credential <span className="text-red-400">*</span>
                </label>
                <select
                                                                                  id="sched-credential"
                  value={formData.credential_id}
                  onChange={(e) => handleCredentialChange(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                >
                  <option value="">Select credential...</option>
                  {credentials.filter(c => c.is_enabled).map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.provider})</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="sched-model" className="block text-sm font-medium text-gray-300 mb-1">
                  Model <span className="text-red-400">*</span>
                </label>
                <select
                                                                                  id="sched-model"
                  value={formData.model_id}
                  onChange={(e) => setFormData({ ...formData, model_id: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  required
                  disabled={!formData.credential_id}
                >
                  <option value="">Select model...</option>
                  {filteredModels.map(m => (
                    <option key={m.id} value={m.id}>{m.name || m.id}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Scoring Configuration Section */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Scoring Configuration</h4>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="sched-scoring-method" className="block text-sm font-medium text-gray-300 mb-1">
                    Scoring Method
                  </label>
                  <select
                                                                                  id="sched-scoring-method"
                    value={formData.scoring_method}
                    onChange={(e) => setFormData({ ...formData, scoring_method: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  >
                    {SCORING_METHODS.map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                  </select>
                  {/* Scoring Method Guidance */}
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.scoring_method === 'exact_match' && 'Response must exactly match expected output.'}
                    {formData.scoring_method === 'contains' && 'Expected output must appear in the response.'}
                    {formData.scoring_method === 'similarity' && 'Uses Levenshtein distance to compare strings.'}
                    {formData.scoring_method === 'semantic_similarity' && 'Uses AI embeddings to compare meaning.'}
                    {formData.scoring_method === 'llm_judge' && 'AI evaluator scores response quality.'}
                  </p>
                </div>
                <div>
                  <label htmlFor="pass-threshold" className="flex justify-between text-sm text-gray-300 mb-1">
                    <span>Pass Threshold</span>
                    <span className="text-gray-400">{(formData.pass_threshold * 100).toFixed(0)}%</span>
                  </label>
                  <input id="pass-threshold"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={formData.pass_threshold}
                    onChange={(e) => setFormData({ ...formData, pass_threshold: Number.parseFloat(e.target.value) })}
                    className="w-full mt-2"
                  />
                </div>
              </div>

              {/* LLM Judge Rubric */}
              {formData.scoring_method === 'llm_judge' && (
                <div>
                  <label htmlFor="sched-judge-rubric" className="block text-sm font-medium text-gray-300 mb-1">
                    Judge Rubric
                  </label>
                  <select
                                                                                  id="sched-judge-rubric"
                    value={formData.judge_rubric_id}
                    onChange={(e) => setFormData({ ...formData, judge_rubric_id: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  >
                    <option value="">Default rubric</option>
                    {rubrics.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Semantic Similarity Threshold */}
              {formData.scoring_method === 'semantic_similarity' && (
                <div>
                  <label htmlFor="similarity-threshold" className="flex justify-between text-sm text-gray-300 mb-1">
                    <span>Similarity Threshold</span>
                    <span className="text-gray-400">{(formData.similarity_threshold * 100).toFixed(0)}%</span>
                  </label>
                  <input id="similarity-threshold"
                    type="range"
                    min="0.5"
                    max="1"
                    step="0.05"
                    value={formData.similarity_threshold}
                    onChange={(e) => setFormData({ ...formData, similarity_threshold: Number.parseFloat(e.target.value) })}
                    className="w-full"
                  />
                </div>
              )}

              {/* Run Assertions Toggle */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, run_assertions: !formData.run_assertions })}
                  className={`w-10 h-6 rounded-full transition-colors ${
                    formData.run_assertions ? 'bg-blue-500' : 'bg-gray-600'
                  }`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                    formData.run_assertions ? 'translate-x-5' : 'translate-x-1'
                  }`} />
                </button>
                <span className="text-gray-300">Run test case assertions</span>
              </div>
            </div>
          </div>

          {/* Schedule Configuration Section */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Schedule Configuration</h4>
            <div>
              <label htmlFor="sched-frequency" className="block text-sm font-medium text-gray-300 mb-1">
                Frequency
              </label>
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(FREQUENCY_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData({ ...formData, frequency: key })}
                    className={`p-2 rounded border text-center text-sm transition-colors ${
                      formData.frequency === key
                        ? 'border-blue-500 bg-blue-500/20 text-blue-400'
                        : 'border-gray-600 bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time/Day settings for daily/weekly */}
            {(['daily', 'weekly'].includes(formData.frequency)) && (
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label htmlFor="sched-time" className="block text-sm font-medium text-gray-300 mb-1">
                    Time <span className="text-gray-500 font-normal">(UTC)</span>
                  </label>
                  <input
                    id="sched-time"
                    type="time"
                    value={formData.schedule_time}
                    onChange={(e) => setFormData({ ...formData, schedule_time: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Displays in your local time after saving
                  </p>
                </div>
                {formData.frequency === 'weekly' && (
                  <div>
                    <label htmlFor="sched-day" className="block text-sm font-medium text-gray-300 mb-1">
                      Day
                    </label>
                    <select
                                                                                  id="sched-day"
                      value={formData.schedule_day}
                      onChange={(e) => setFormData({ ...formData, schedule_day: Number.parseInt(e.target.value) })}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white"
                    >
                      {weekdays.map((day, i) => (
                        <option key={day} value={i}>{day}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Drift Detection Thresholds */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Drift Detection Thresholds
            </h4>
            <div className="space-y-3">
              <div>
                <label htmlFor="pass-rate-drop-threshold" className="flex justify-between text-sm text-gray-400">
                  <span>Pass Rate Drop</span>
                  <span>{(formData.drift_pass_rate_threshold * 100).toFixed(0)}%</span>
                </label>
                <input id="pass-rate-drop-threshold"
                  type="range"
                  min="0.01"
                  max="0.5"
                  step="0.01"
                  value={formData.drift_pass_rate_threshold}
                  onChange={(e) => setFormData({ ...formData, drift_pass_rate_threshold: Number.parseFloat(e.target.value) })}
                  className="w-full"
                />
              </div>
              <div>
                <label htmlFor="latency-increase-threshold" className="flex justify-between text-sm text-gray-400">
                  <span>Latency Increase</span>
                  <span>{(formData.drift_latency_threshold * 100).toFixed(0)}%</span>
                </label>
                <input id="latency-increase-threshold"
                  type="range"
                  min="0.1"
                  max="2"
                  step="0.1"
                  value={formData.drift_latency_threshold}
                  onChange={(e) => setFormData({ ...formData, drift_latency_threshold: Number.parseFloat(e.target.value) })}
                  className="w-full"
                />
              </div>
              <div>
                <label htmlFor="cost-increase-threshold" className="flex justify-between text-sm text-gray-400">
                  <span>Cost Increase</span>
                  <span>{(formData.drift_cost_threshold * 100).toFixed(0)}%</span>
                </label>
                <input id="cost-increase-threshold"
                  type="range"
                  min="0.1"
                  max="2"
                  step="0.1"
                  value={formData.drift_cost_threshold}
                  onChange={(e) => setFormData({ ...formData, drift_cost_threshold: Number.parseFloat(e.target.value) })}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Alert on Drift */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, notify_on_drift: !formData.notify_on_drift })}
              className={`w-10 h-6 rounded-full transition-colors ${
                formData.notify_on_drift ? 'bg-blue-500' : 'bg-gray-600'
              }`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                formData.notify_on_drift ? 'translate-x-5' : 'translate-x-1'
              }`} />
            </button>
            <span className="text-gray-300">Alert when drift is detected</span>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
            >
              {schedule ? 'Save Changes' : 'Create Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SchedulesTab;
